import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * docs/06-FRONTEND-SPEC.md §9 — the single helper for conditional classes.
 *
 * `twMerge` is what makes component overrides safe: a caller passing
 * `className="px-8"` to a component whose base is `px-4` gets `px-8`, not both
 * in undefined order.
 *
 * It has to be told about our scales. tailwind-merge decides which classes
 * conflict from a built-in list of Tailwind's default values, and anything it
 * does not recognise it guesses at — usually wrongly, and always silently.
 * With the §2.1 type scale unregistered it read `text-body-sm` as a *colour*
 * and dropped `text-white` from `bg-navy-600 text-white text-body-sm`, so
 * every primary button rendered ink-on-navy at 1.54:1. Nothing errored; the
 * class simply vanished.
 *
 * Every custom key added to `tailwind-preset.js` must be mirrored here.
 * `cn.test.ts` guards the pairs that actually bit.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // §2.1 type scale
      'font-size': [
        {
          text: [
            'display-1',
            'display-2',
            'h1',
            'h2',
            'h3',
            'h4',
            'body-lg',
            'body',
            'body-sm',
            'caption',
            'overline',
            'stat',
            'stat-label',
          ],
        },
      ],
      // §1.3 semantic text colours
      'text-color': [
        { text: ['primary', 'secondary', 'muted', 'inverse', 'accent', 'link', 'danger'] },
      ],
      // §1.3 surfaces and §1.4 accent tints
      'bg-color': [
        {
          bg: [
            'page',
            'surface',
            'surface-alt',
            'inverse',
            'accent-tint-teal',
            'accent-tint-violet',
            'accent-tint-amber',
            'accent-tint-navy',
            'accent-tint-rose',
            'accent-tint-sky',
            'accent-tint-lime',
            'accent-tint-slate',
          ],
        },
      ],
      'border-color': [{ border: ['subtle', 'strong'] }],
      // §6 motion
      duration: [{ duration: ['fast', 'base', 'slow'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
